import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import type { ROICalculatorInputs, ROICalculatorResults } from '../types';

interface ROICalculatorProps {
  industryName: string;
}

const ROICalculator: React.FC<ROICalculatorProps> = ({ industryName }) => {
  const [inputs, setInputs] = useState<ROICalculatorInputs>({
    employeeCount: 10,
    avgHourlyRate: 400,
    hoursSpentOnTasks: 20,
    tasksPerWeek: 50,
  });

  const [results, setResults] = useState<ROICalculatorResults | null>(null);
  const [showResults, setShowResults] = useState(false);

  const calculateROI = () => {
    const weeklyHours = inputs.hoursSpentOnTasks;
    const monthlyHours = weeklyHours * 4;
    const currentMonthlyCost = monthlyHours * inputs.avgHourlyRate * inputs.employeeCount;
    
    const automationSavingsRate = 0.65;
    const potentialMonthlySavings = currentMonthlyCost * automationSavingsRate;
    const annualSavings = potentialMonthlySavings * 12;
    const hoursReclaimed = monthlyHours * automationSavingsRate;
    
    const automationCost = 50000;
    const roi = ((annualSavings - automationCost) / automationCost) * 100;

    setResults({
      currentMonthlyCost,
      potentialMonthlySavings,
      annualSavings,
      hoursReclaimed,
      roi,
    });
    setShowResults(true);
  };

  const handleInputChange = (field: keyof ROICalculatorInputs, value: string) => {
    const numValue = parseFloat(value) || 0;
    setInputs((prev) => ({ ...prev, [field]: numValue }));
    setShowResults(false);
  };

  return (
    <div className="bg-card rounded-lg shadow-lg border border-border p-6 lg:p-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
          <Icon name="Calculator" size={24} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-text-primary">ROI Calculator</h3>
          <p className="text-sm text-text-secondary">Calculate your potential savings</p>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <Input
          name="employeeCount"
          autoComplete="off"
          label="Number of Employees"
          type="number"
          value={inputs.employeeCount.toString()}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('employeeCount', e.target.value)}
          placeholder="10"
        />
        <Input
          name="avgHourlyRate"
          autoComplete="off"
          label="Average Hourly Rate (₹)"
          type="number"
          value={inputs.avgHourlyRate.toString()}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('avgHourlyRate', e.target.value)}
          placeholder="25"
        />
        <Input
          name="hoursSpentOnTasks"
          autoComplete="off"
          label="Hours Spent on Manual Tasks (per week)"
          type="number"
          value={inputs.hoursSpentOnTasks.toString()}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('hoursSpentOnTasks', e.target.value)}
          placeholder="20"
        />
        <Input
          name="tasksPerWeek"
          autoComplete="off"
          label="Repetitive Tasks per Week"
          type="number"
          value={inputs.tasksPerWeek.toString()}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('tasksPerWeek', e.target.value)}
          placeholder="50"
        />
      </div>

      <Button variant="default" fullWidth onClick={calculateROI} iconName="TrendingUp" iconPosition="left">
        Calculate Savings
      </Button>

      {showResults && results && (
        <div className="mt-6 pt-6 border-t border-border space-y-4 animate-fade-in">
          <div className="bg-success/10 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Monthly Savings</span>
              <Icon name="TrendingUp" size={20} className="text-success" />
            </div>
            <div className="text-2xl font-bold text-success">
              ₹{results.potentialMonthlySavings.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-surface rounded-lg p-4">
              <div className="text-sm text-text-secondary mb-1">Annual Savings</div>
              <div className="text-xl font-bold text-primary">
                ₹{results.annualSavings.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="bg-surface rounded-lg p-4">
              <div className="text-sm text-text-secondary mb-1">Hours Reclaimed</div>
              <div className="text-xl font-bold text-primary">
                {results.hoursReclaimed.toFixed(0)}/mo
              </div>
            </div>
          </div>

          <div className="bg-accent/10 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Return on Investment</span>
              <Icon name="Award" size={20} className="text-accent" />
            </div>
            <div className="text-2xl font-bold text-accent">{results.roi.toFixed(0)}%</div>
            <p className="text-xs text-text-secondary mt-2">
              Based on industry averages for {industryName}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ROICalculator;